// Endpoints tipados do ms-meta-reports. Cada função recebe filtros (Filters)
// e converte pra query params via `filtersToParams`. Retorna a resposta
// tipada do API-SPEC.

import reportsClient from "./reportsClient";
import { filtersToParams } from "../lib/filters";
import type {
    Filters,
    OverviewResponse,
    FlowsResponse,
    FlowReportResponse,
    NodeDetailResponse,
    StuckLeadsResponse,
    BroadcastsListResponse,
    BroadcastReportResponse,
    GroupedBroadcastReportResponse,
    EsteiraListResponse,
    EsteiraReportResponse,
    ClicksResponse,
    CountriesResponse,
    FilterOptionsResponse,
} from "../types/reports";

interface ClickScope {
    type: "all" | "flow" | "esteira" | "broadcast";
    id?: string | null;
}

type SortBy = "revenue" | "ctr" | "sends";

async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    const { data } = await reportsClient.get<T>(path, { params });
    return data;
}

export const reportsApi = {
    ping: () => get<unknown>("/api/reports/ping"),
    health: () => get<unknown>("/healthz"),

    // Front consulta pra decidir se renderiza o item "Dashboard" no menu
    // e se permite acessar /meta/dashboard. Sem precisar bater nos endpoints
    // pesados antes.
    dashboardAccess: () => get<{ enabled: boolean }>("/api/me/dashboard-access"),

    overview: (filters: Filters) =>
        get<OverviewResponse>("/api/reports/overview", filtersToParams(filters)),

    flows: (filters: Filters) =>
        get<FlowsResponse>("/api/reports/flows", filtersToParams(filters)),

    flow: (flowId: string, filters: Filters) =>
        get<FlowReportResponse>(`/api/reports/flows/${flowId}`, filtersToParams(filters)),

    node: (flowId: string, nodeId: string, filters: Filters) =>
        get<NodeDetailResponse>(
            `/api/reports/flows/${flowId}/nodes/${nodeId}`,
            filtersToParams(filters)
        ),

    stuckLeads: (
        flowId: string,
        nodeId: string,
        opts: { limit?: number; offset?: number; filter?: string } = {}
    ) =>
        get<StuckLeadsResponse>(
            `/api/reports/flows/${flowId}/nodes/${nodeId}/stuck-leads`,
            { limit: opts.limit ?? 50, offset: opts.offset ?? 0, filter: opts.filter }
        ),

    broadcasts: (
        filters: Filters,
        page = 1,
        pageSize = 24,
        groupBy: "none" | "template" = "none"
    ) =>
        get<BroadcastsListResponse>("/api/reports/broadcasts", {
            ...filtersToParams(filters),
            page,
            pageSize,
            groupBy,
        }),

    broadcast: (bcId: string, filters: Filters) =>
        get<BroadcastReportResponse>(
            `/api/reports/broadcasts/${bcId}`,
            filtersToParams(filters)
        ),

    broadcastGrouped: (contentHash: string, filters: Filters) =>
        get<GroupedBroadcastReportResponse>(
            `/api/reports/broadcasts/grouped/${contentHash}`,
            filtersToParams(filters)
        ),

    esteira: (filters: Filters) =>
        get<EsteiraListResponse>("/api/reports/esteira", filtersToParams(filters)),

    esteiraGroup: (esId: string, filters: Filters, sortBy: SortBy = "revenue") =>
        get<EsteiraReportResponse>(`/api/reports/esteira/${esId}`, {
            ...filtersToParams(filters),
            sortBy,
        }),

    clicks: (filters: Filters, scope: ClickScope = { type: "all" }) =>
        get<ClicksResponse>("/api/reports/clicks", {
            ...filtersToParams(filters),
            scopeType: scope.type,
            scopeId: scope.id || undefined,
        }),

    countries: (filters: Filters) =>
        get<CountriesResponse>("/api/reports/countries", filtersToParams(filters)),

    filterOptions: () =>
        get<FilterOptionsResponse>("/api/reports/filters/options"),
};

export type { ClickScope, SortBy };
